/**
 * Adapted from PlayCanvas Engine's gsplat-hybrid-renderer.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { syncGSplatMaterialTemplate } from '@/plugins/gsplat/effects/GSplatMaterialTemplateSync'
import type {
  GSplatBufferLeaf,
  GSplatDrawObjectLeaf,
  GSplatIndirectSlotRange,
  GSplatMaterialLeaf,
  GSplatMaterialSnapshot,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { Disposable } from '@/shared/types/core'
import type { GSplatHybridRendererScratch } from './GSplatHybridRendererScratch'
import {
  GSPLAT_RENDER_MODE_FORWARD,
  GSPLAT_RENDER_MODE_NONE,
  type GSplatRenderPassMask,
  hasGSplatRenderPass,
} from './GSplatPipelineTypes'
import type { GSplatProjectorDispatchInput } from './GSplatProjector'
import {
  GSPLAT_INSTANCE_SIZE,
  GSplatRenderer,
  type GSplatRendererOptions,
  type GSplatRendererWorld,
  type GSplatRendererWorldState,
  type GSplatRenderViewParams,
} from './GSplatRenderer'
import type { GSplatWorld } from './world/GSplatWorld'
import type { GSplatWorldState } from './world/GSplatWorldState'

export interface GSplatHybridInvalidatableLeaf extends Disposable {
  invalidateUpload(): void
}

export interface GSplatHybridGpuSorter extends Disposable {
  prepareIndirect(): Uint32Array
  /** Concrete implementation selected by the PlayCanvas-aligned radix facade. */
  readonly resolvedKind?: 'portable' | 'onesweep'
  readonly radixBits?: number
  sortIndirect?(
    keysBuffer: GSplatBufferLeaf,
    maxElementCount: number,
    numBits: number,
    dispatchRange: GSplatIndirectSlotRange,
    sortSlotBase: number,
    sortElementCountBuffer: GSplatBufferLeaf,
    options?: Readonly<{
      initialValues?: GSplatBufferLeaf
      skipLastPassKeyWrite?: boolean
      destructiveKeys?: boolean
    }>
  ): GSplatBufferLeaf
}

export interface GSplatHybridProjectorLeaf extends Disposable {
  readonly projectionCache?: GSplatBufferLeaf | null
  readonly pickIds?: GSplatBufferLeaf | null
  readonly sortKeys?: GSplatBufferLeaf | null
  setPickIdsEnabled?(enabled: boolean): void
  dispatch?(input: GSplatProjectorDispatchInput): void
  writeIndirectArgs?(
    drawRange: GSplatIndirectSlotRange,
    dispatchRange: GSplatIndirectSlotRange,
    sortSlotBase: number,
    numSplatsBuffer: GSplatBufferLeaf,
    sortElementCountBuffer: GSplatBufferLeaf,
    sortIndirectInfo: Uint32Array
  ): void
}

export interface GSplatHybridIntervalCompactionLeaf extends GSplatHybridInvalidatableLeaf {
  readonly compactedSplatIds?: GSplatBufferLeaf | null
  readonly numSplatsBuffer?: GSplatBufferLeaf
  readonly sortElementCountBuffer?: GSplatBufferLeaf
  uploadIntervals?(worldState: GSplatWorldState): void
  dispatchCompact?(
    frustumCuller: GSplatWorld['workBuffer']['frustumCuller'],
    numIntervals: number,
    totalActiveSplats: number,
    fisheyeEnabled: boolean
  ): void
  writeIndirectArgs(
    drawRange: GSplatIndirectSlotRange,
    dispatchRange: GSplatIndirectSlotRange,
    numIntervals: number,
    sortIndirectInfo: Uint32Array
  ): void
}

export interface GSplatHybridPipelineParts {
  readonly gpuSorter: GSplatHybridGpuSorter
  readonly projector: GSplatHybridProjectorLeaf
  readonly intervalCompaction: GSplatHybridIntervalCompactionLeaf
}

export interface GSplatHybridPickLeaves {
  readonly material: GSplatMaterialLeaf
  readonly drawObject: GSplatDrawObjectLeaf
}

/**
 * Forward frame 中已经完成的 Hybrid GPU 资源快照。
 *
 * Picker 只借用这组 immutable-by-convention 的资源做私有 ID raster，不再重复执行
 * interval compaction、projector 和 radix sort。快照在每次 forward prepare 中更新；调用方
 * 不得跨 snapshot 生命周期缓存这些 buffer。原始 splat ID 与投影 entry 一起写入 cache，
 * 避免独立 pickIds buffer 和相机变化后第二套完整排序流程造成的掉帧。
 */
export interface GSplatHybridForwardPreparedView {
  readonly stateVersion: number
  readonly drawSlot: number
  readonly sortedIndices: GSplatBufferLeaf
  readonly projectionCache: GSplatBufferLeaf
  readonly numSplatsBuffer: GSplatBufferLeaf
  readonly pickIds?: GSplatBufferLeaf
  readonly viewportSize: THREE.Vector4
  readonly cameraMatrixWorld: THREE.Matrix4
  readonly cameraProjectionMatrix: THREE.Matrix4
  readonly alphaClip: number
  readonly alphaClipForward: number
  readonly cacheStride: number
}

export interface GSplatHybridPipelineFactory {
  create(): GSplatHybridPipelineParts
  createPickLeaves?(renderer: GSplatHybridRenderer): GSplatHybridPickLeaves
  prepareRenderView?(
    renderer: GSplatHybridRenderer,
    world: GSplatRendererWorld,
    worldState: GSplatRendererWorldState,
    params: GSplatRenderViewParams
  ): boolean
  preparePickingView?(
    renderer: GSplatHybridRenderer,
    world: GSplatRendererWorld,
    worldState: GSplatRendererWorldState,
    params: GSplatRenderViewParams
  ): GSplatDrawObjectLeaf | null
}

export interface GSplatHybridRendererOptions extends GSplatRendererOptions {
  readonly material: GSplatMaterialLeaf
  readonly drawObject: GSplatDrawObjectLeaf
  readonly scratch?: GSplatHybridRendererScratch | null
  readonly pipelineFactory?: GSplatHybridPipelineFactory | null
  readonly gpuPickIdMode?: 'direct' | 'buffer'
}

/** WebGPU compute preprocess + quad raster 的 PlayCanvas 对齐 renderer。 */
export class GSplatHybridRenderer extends GSplatRenderer {
  readonly meshInstance: GSplatDrawObjectLeaf
  readonly scratch: GSplatHybridRendererScratch | null
  gpuSorter: GSplatHybridGpuSorter | null = null
  projector: GSplatHybridProjectorLeaf | null = null
  intervalCompaction: GSplatHybridIntervalCompactionLeaf | null = null
  indirectDrawSlot = -1
  indirectDispatchSlot = -1
  lastCompactedNumIntervals = 0

  private readonly internalMaterial: GSplatMaterialLeaf
  private readonly pipelineFactory: GSplatHybridPipelineFactory | null
  private pickMaterialValue: GSplatMaterialLeaf | null = null
  private pickMeshInstanceValue: GSplatDrawObjectLeaf | null = null
  private indirectDrawRange: GSplatIndirectSlotRange | null = null
  private indirectDispatchRange: GSplatIndirectSlotRange | null = null
  private cacheStride = 9
  readonly gpuPickIdMode: 'direct' | 'buffer'
  private readonly internalDefines = new Set<string>()
  private lastSourceChunksKey = ''
  private sourceMaterialVersion = -1
  private sourceTemplateRevision = -1
  private readonly viewportSize = new THREE.Vector4(1, 1, 1, 1)
  private readonly cameraPosition = new THREE.Vector3()
  private readonly cameraDirection = new THREE.Vector3()
  private readonly aabbCorner = new THREE.Vector3()
  private forwardPreparedView: GSplatHybridForwardPreparedView | null = null

  private withGpuStage<T>(stage: string, callback: () => T): T {
    return this.adapter.withGpuStage ? this.adapter.withGpuStage(stage, callback) : callback()
  }

  constructor(options: GSplatHybridRendererOptions) {
    super(options)
    this.internalMaterial = options.material
    this.meshInstance = options.drawObject
    this.scratch = options.scratch ?? null
    this.pipelineFactory = options.pipelineFactory ?? null
    this.gpuPickIdMode = options.gpuPickIdMode ?? 'direct'
    this.configureMaterial()
    for (const [name] of this.internalMaterial.captureSnapshot(this.adapter.language).defines) {
      this.internalDefines.add(name)
    }
    for (const name of [
      '{GSPLAT_INSTANCE_SIZE}',
      '{CACHE_STRIDE}',
      'GSPLAT_INDIRECT_DRAW',
      'GSPLAT_UNIFIED_ID',
      'PICK_CUSTOM_ID',
      'GSPLAT_NO_FOG',
      'GSPLAT_OVERDRAW',
    ]) {
      this.internalDefines.add(name)
    }
    this.meshInstance.visible = false
    this.meshInstance.instancingCount = 0
    this.meshInstance.castShadow = false
    this.meshInstance.pickable = false
    this.meshInstance.setVisibilityFilter((camera) => {
      const renderMode = this.renderMode ?? GSPLAT_RENDER_MODE_NONE
      return (
        camera === this.cameraNode && hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_FORWARD)
      )
    })
  }

  override get material(): GSplatMaterialLeaf {
    return this.internalMaterial
  }

  override get usesGpuSort(): boolean {
    return true
  }

  override get requiresBounds(): boolean {
    return true
  }

  get pickMaterial(): GSplatMaterialLeaf | null {
    return this.pickMaterialValue
  }

  get pickMeshInstance(): GSplatDrawObjectLeaf | null {
    return this.pickMeshInstanceValue
  }

  /** 返回最近一次 forward prepare 的 GPU 资源；没有完成 forward 快照时返回 null。 */
  getForwardPreparedView(): GSplatHybridForwardPreparedView | null {
    return this.forwardPreparedView
  }

  /**
   * Compatibility hook for callers that used to request a demand-driven pick snapshot.
   * WebGPU Hybrid now publishes the pick-id map during every forward prepare.
   */
  requestForwardPickIds(): void {
    this.assertActive()
  }

  /**
   * 基于 forward snapshot 配置私有 pick leaf。
   *
   * 这里刻意不触发任何 compute。projection cache 中的颜色仍保持 forward 布局，ID 直接从
   * projection cache 的第 9 个 word 读取，避免独立 pickIds buffer 和每帧全量辅助写入。
   * Pick 不再因为相机对象不同而重新执行完整 GPU sorting pipeline。
   */
  prepareForwardPickView(): GSplatDrawObjectLeaf | null {
    this.assertActive()
    const snapshot = this.forwardPreparedView
    if (!snapshot) return null
    this.ensurePickLeaves()
    this.syncPickMaterialSettings()
    const drawObject = this.pickMeshInstanceValue
    const material = this.pickMaterialValue
    if (!drawObject || !material) return null
    this.setHybridSortedRendering(
      snapshot.drawSlot,
      snapshot.sortedIndices,
      snapshot.projectionCache,
      snapshot.numSplatsBuffer,
      drawObject,
      material
    )
    // `compactedSplatIds` is indexed by interval-compaction source position, while the sorted
    // projection cache is indexed by its post-filter destination position. Direct-ID mode reads
    // the original ID stored beside each projected entry.
    material.setParameter('compactedSplatIds', null)
    material.setParameter('pickIds', snapshot.pickIds ?? null)
    // The projection cache stores Gaussian axes in *forward-viewport pixels*. The pick target is
    // intentionally reduced, but that only changes the attachment resolution: NDC still maps
    // the cached world footprint to the smaller target. Replacing viewport_size with the reduced
    // dimensions would divide the same pixel axes by 0.25x and make every Gaussian four times
    // too large, causing the 5x5 ID sample to prefer a neighboring splat. Keep the exact forward
    // viewport contract used when the cache was produced.
    material.setParameter('viewport_size', snapshot.viewportSize.clone())
    material.setParameter('alphaClip', snapshot.alphaClip)
    material.setParameter('alphaClipForward', snapshot.alphaClipForward)
    return drawObject
  }

  override setRenderMode(renderMode: GSplatRenderPassMask): void {
    this.assertActive()
    const oldRenderMode = this.renderMode ?? GSPLAT_RENDER_MODE_NONE
    const wasForward = hasGSplatRenderPass(oldRenderMode, GSPLAT_RENDER_MODE_FORWARD)
    const isForward = hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_FORWARD)
    if (wasForward && !isForward) {
      this.forwardPreparedView = null
    }
    if (wasForward && !isForward) this.layer.removeMeshInstances([this.meshInstance], true)
    if (!wasForward && isForward) this.layer.addMeshInstances([this.meshInstance], true)
    super.setRenderMode(renderMode)
  }

  override destroy(): void {
    if (this.destroyed) return
    if (
      hasGSplatRenderPass(this.renderMode ?? GSPLAT_RENDER_MODE_NONE, GSPLAT_RENDER_MODE_FORWARD)
    ) {
      this.layer.removeMeshInstances([this.meshInstance], true)
    }
    this.gpuSorter?.dispose()
    this.gpuSorter = null
    this.projector?.dispose()
    this.projector = null
    this.intervalCompaction?.dispose()
    this.intervalCompaction = null
    this.internalMaterial.dispose()
    this.pickMaterialValue?.dispose()
    this.pickMaterialValue = null
    this.meshInstance.dispose()
    this.pickMeshInstanceValue?.dispose()
    this.pickMeshInstanceValue = null
    this.forwardPreparedView = null
    super.destroy()
  }

  override onWorkBufferFormatChanged(): void {
    this.forwardPreparedView = null
    this.workBuffer.syncWithFormat()
  }

  override update(count: number, _textureSize: number): void {
    this.assertActive()
    if (!Number.isSafeInteger(count) || count < 0) {
      throw new Error(`非法 GSplat visible count: ${count}`)
    }
    if (this.meshInstance.instancingCount <= 0) this.meshInstance.instancingCount = 1
    this.meshInstance.visible = count > 0
  }

  override invalidateCullUpload(): void {
    this.intervalCompaction?.invalidateUpload()
  }

  /** 与上游同形：分配本 frame 的 draw/连续 dispatch slots，并写入 interval args。 */
  allocateAndWriteIntervalIndirectArgs(numIntervals: number): void {
    this.assertActive()
    if (!Number.isSafeInteger(numIntervals) || numIntervals < 0) {
      throw new Error(`非法 GSplat interval count: ${numIntervals}`)
    }
    if (!this.gpuSorter || !this.intervalCompaction) {
      throw new Error('GSplatHybridRenderer interval pipeline 尚未准备')
    }
    const sortInfo = this.gpuSorter.prepareIndirect()
    if (sortInfo.length !== 4 || sortInfo[0] < 1 || sortInfo[0] > 3) {
      throw new Error('GSplatHybridRenderer sorter indirect metadata 非法')
    }
    for (let index = 1; index <= sortInfo[0]; index++) {
      if (sortInfo[index] < 1) throw new Error(`GSplatHybridRenderer sorter slot ${index - 1} 非法`)
    }

    const drawRange = this.adapter.allocateIndirectDrawSlots(1)
    const dispatchRange = this.adapter.allocateIndirectDispatchSlots(1 + sortInfo[0])
    this.intervalCompaction.writeIndirectArgs(drawRange, dispatchRange, numIntervals, sortInfo)
    this.indirectDrawRange = drawRange
    this.indirectDispatchRange = dispatchRange
    this.indirectDrawSlot = drawRange.base
    this.indirectDispatchSlot = dispatchRange.base
    this.lastCompactedNumIntervals = numIntervals
  }

  override prepareRenderView(
    world: GSplatRendererWorld,
    worldState: GSplatRendererWorldState,
    params: GSplatRenderViewParams
  ): boolean {
    this.assertActive()
    this.internalMaterial.setParameter(
      'gsplatShaderEffectEnabled',
      params.shaderEffectEnabled === true
    )
    if (worldState.totalActiveSplats <= 0) {
      this.forwardPreparedView = null
      return false
    }
    this.ensureGpuPipeline()
    const customPrepare = this.pipelineFactory?.prepareRenderView
    const prepare = () =>
      customPrepare
        ? customPrepare(this, world, worldState, params)
        : this.prepareHybridRenderView(world as GSplatWorld, worldState as GSplatWorldState, params)
    // Indirect draw slots are frame-scoped by the adapter.  A cached projection/sort snapshot
    // cannot be rebound directly on a later frame because its draw slot is retired at frame end.
    // Keep the snapshot for picker coordination, but always rebuild the current-frame indirect
    // binding here until a persistent-slot path is introduced deliberately.
    // PlayCanvas records compaction, projector and radix into one frame command buffer. Three's
    // public renderer.compute() submits immediately, so the adapter collects the same ordered node
    // sequence and submits one stable compute group instead of seven command buffers per frame.
    return this.adapter.withComputeBatch ? this.adapter.withComputeBatch(prepare) : prepare()
  }

  override preparePickingView(
    world: GSplatRendererWorld,
    worldState: GSplatRendererWorldState,
    params: GSplatRenderViewParams
  ): GSplatDrawObjectLeaf | null {
    this.assertActive()
    if (worldState.totalActiveSplats <= 0) return null
    this.ensureGpuPipeline()
    this.ensurePickLeaves()
    this.syncPickMaterialSettings()
    // Picking uses the same transient compaction/projector/sort resources as forward rendering;
    // force the next forward frame to publish a fresh cache after the private pass.
    const customPrepare = this.pipelineFactory?.preparePickingView
    const prepare = (): GSplatDrawObjectLeaf | null => {
      if (customPrepare) return customPrepare(this, world, worldState, params)
      return this.prepareHybridRenderView(
        world as GSplatWorld,
        worldState as GSplatWorldState,
        params,
        true
      )
        ? this.pickMeshInstanceValue
        : null
    }
    return this.adapter.withComputeBatch ? this.adapter.withComputeBatch(prepare) : prepare()
  }

  override setCpuSortedRendering(): void {
    this.assertActive()
    this.meshInstance.setIndirect(-1)
    this.meshInstance.visible = false
  }

  override setOrderData(): void {
    // Hybrid 只消费 projection cache + storage sorted indices，不绑定 CPU order。
  }

  override frameUpdate(params: GSplatRenderViewParams): void {
    this.assertActive()
    this.internalMaterial.setParameter('alphaClip', params.alphaClip)
    this.internalMaterial.setParameter('alphaClipForward', params.alphaClipForward)
    const width = Math.max(1, Math.floor((params.width ?? 1) * (params.pixelRatio ?? 1)))
    const height = Math.max(1, Math.floor((params.height ?? 1) * (params.pixelRatio ?? 1)))
    this.viewportSize.set(width, height, 1 / width, 1 / height)
    this.internalMaterial.setParameter('viewport_size', this.viewportSize)
    const nextCacheStride = 8 + params.varyings.words
    if (nextCacheStride !== this.cacheStride) {
      this.cacheStride = nextCacheStride
      this.internalMaterial.setDefine('{CACHE_STRIDE}', nextCacheStride)
      this.internalMaterial.update()
    }
    if (params.materialTemplate) {
      if (this.sourceTemplateRevision !== params.materialTemplate.revision) {
        syncGSplatMaterialTemplate(this.internalMaterial, params.materialTemplate)
        this.sourceTemplateRevision = params.materialTemplate.revision
      }
    } else if (this.sourceMaterialVersion !== params.material.revision) {
      this.copyMaterialSettings(params.material)
      this.sourceMaterialVersion = params.material.revision
    }
    this.internalMaterial.setParameter(
      'gsplatShaderEffectEnabled',
      params.shaderEffectEnabled === true
    )
  }

  protected override setRasterLeafBounds(aabb: THREE.Box3 | null): void {
    this.meshInstance.setBounds?.(aabb)
  }

  private ensureGpuPipeline(): void {
    if (this.gpuSorter && this.projector && this.intervalCompaction) return
    if (!this.pipelineFactory) {
      throw new Error('GSplatHybridRenderer 缺少 GPU pipeline factory')
    }
    const staged = this.pipelineFactory.create()
    try {
      if (!staged.gpuSorter || !staged.projector || !staged.intervalCompaction) {
        throw new Error('GSplatHybridRenderer GPU pipeline 不完整')
      }
      this.gpuSorter = staged.gpuSorter
      this.projector = staged.projector
      this.intervalCompaction = staged.intervalCompaction
    } catch (error) {
      staged.gpuSorter?.dispose()
      staged.projector?.dispose()
      staged.intervalCompaction?.dispose()
      throw error
    }
  }

  private ensurePickLeaves(): void {
    if (this.pickMaterialValue && this.pickMeshInstanceValue) return
    const staged = this.pipelineFactory?.createPickLeaves?.(this)
    if (!staged) throw new Error('GSplatHybridRenderer 缺少 lazy pick leaf factory')
    try {
      if (!staged.material || !staged.drawObject) {
        throw new Error('GSplatHybridRenderer pick leaves 不完整')
      }
      staged.drawObject.visible = false
      staged.drawObject.instancingCount = 1
      staged.drawObject.castShadow = false
      staged.drawObject.pickable = false
      this.pickMaterialValue = staged.material
      this.pickMeshInstanceValue = staged.drawObject
    } catch (error) {
      staged.material?.dispose()
      staged.drawObject?.dispose()
      throw error
    }
  }

  private prepareHybridRenderView(
    world: GSplatWorld,
    worldState: GSplatWorldState,
    params: GSplatRenderViewParams,
    pickMode = false
  ): boolean {
    // Projector 必须消费当前 View 的 renderer-private material：它既包含从共享模板同步的
    // effect chunks/uniforms，也包含 shaderEffectEnabled 这类按 View 计算的临时参数。
    // 直接传 params.material 会丢失 View 私有门禁，使 Hybrid compute 无声跳过普通特效。
    this.internalMaterial.setParameter(
      'gsplatShaderEffectEnabled',
      params.shaderEffectEnabled === true
    )
    const camera = params.cameraNode
    if (!(camera instanceof THREE.Camera)) {
      throw new Error('GSplatHybridRenderer cameraNode 必须为 Three.Camera')
    }
    const sorter = this.requireGpuSorter()
    const projector = this.requireProjector()
    const compaction = this.requireIntervalCompaction()
    const totalActiveSplats = worldState.totalActiveSplats
    if (totalActiveSplats <= 0) return false

    compaction.uploadIntervals(worldState)
    const frustumCuller = world.workBuffer.frustumCuller
    frustumCuller.updateTransformsData(worldState.boundsGroups)
    frustumCuller.computeFrustumPlanes(camera)
    const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : 0
    this.fisheyeProj.update(this.resolveFisheye(params.fisheye), fov, camera.projectionMatrix)
    if (this.fisheyeProj.enabled) {
      camera.updateMatrixWorld(true)
      camera.getWorldPosition(this.cameraPosition)
      this.cameraDirection.setFromMatrixColumn(camera.matrixWorld, 2).normalize()
      frustumCuller.setFisheyeData(
        this.cameraPosition,
        this.cameraDirection,
        this.fisheyeProj.maxTheta
      )
    }

    this.withGpuStage('gsplat.compaction', () => {
      compaction.dispatchCompact(
        frustumCuller,
        worldState.totalIntervals,
        totalActiveSplats,
        this.fisheyeProj.enabled
      )
      this.allocateAndWriteIntervalIndirectArgs(worldState.totalIntervals)
    })
    const drawRange = this.indirectDrawRange
    const dispatchRange = this.indirectDispatchRange
    const compactedSplatIds = compaction.compactedSplatIds
    if (!drawRange || !dispatchRange || !compactedSplatIds) {
      throw new Error('GSplatHybridRenderer indirect/compacted storage 未准备')
    }

    const rawBits = Math.max(
      10,
      Math.min(20, Math.round(Math.log2(Math.max(1, totalActiveSplats / 4))))
    )
    const roundedNumBits = Math.ceil(rawBits / sorter.radixBits) * sorter.radixBits
    const { minDist, maxDist } = this.computeDistanceRange(worldState, camera, params.radialSorting)
    const pixelRatio = params.pixelRatio ?? 1
    const viewportWidth = Math.max(1, Math.floor((params.width ?? 1) * pixelRatio))
    const viewportHeight = Math.max(1, Math.floor((params.height ?? 1) * pixelRatio))
    this.viewportSize.set(viewportWidth, viewportHeight, 1 / viewportWidth, 1 / viewportHeight)
    this.internalMaterial.setParameter('viewport_size', this.viewportSize)
    this.internalMaterial.setParameter('alphaClip', params.alphaClip)
    this.internalMaterial.setParameter('alphaClipForward', params.alphaClipForward)
    const sortInfo = sorter.prepareIndirect()
    const sortSlotBase = dispatchRange.base + 1

    // The projector destination index is not the same as the interval-compacted source index:
    // visibility/alpha filtering and workgroup reservation can remove entries before they are
    // appended to projectionCache. Keep the destination-index -> original-splat-id side buffer
    // synchronized with every forward projection so picking can consume the visible frame
    // without launching a second full compaction/projector/radix chain.
    const captureForwardPick = !pickMode && this.gpuPickIdMode === 'buffer'
    projector.setPickIdsEnabled?.(captureForwardPick)

    this.withGpuStage('gsplat.projector', () => {
      projector.dispatch({
        workBuffer: world.workBuffer,
        camera,
        compactedSplatIds,
        sortElementCountBuffer: compaction.sortElementCountBuffer,
        totalCapacity: totalActiveSplats,
        radialSort: params.radialSorting,
        numBits: roundedNumBits,
        minDist,
        maxDist,
        alphaClip: Math.max(1 / 255, pickMode ? params.alphaClip : params.alphaClipForward),
        minPixelSize: params.minPixelSize * 0.5,
        minContribution: params.minContribution,
        foveationStrength: params.foveationStrength,
        foveationCenter: params.foveationCenter,
        viewportWidth,
        viewportHeight,
        pickMode,
        fisheyeProjection: this.fisheyeProj,
        antiAlias: params.antiAlias,
        material: this.internalMaterial,
        userCacheWords: params.varyings.words,
        sceneOrigin: params.sceneOrigin,
      })
      projector.writeIndirectArgs(
        drawRange,
        dispatchRange,
        sortSlotBase,
        compaction.numSplatsBuffer,
        compaction.sortElementCountBuffer,
        sortInfo
      )
    })
    const sortKeys = projector.sortKeys
    const projectionCache = projector.projectionCache
    if (!sortKeys || !projectionCache) {
      throw new Error('GSplatHybridRenderer projector output 未准备')
    }
    const radixStage = sorter.resolvedKind ? `gsplat.radix.${sorter.resolvedKind}` : 'gsplat.radix'
    const sortedIndices = this.withGpuStage(radixStage, () =>
      sorter.sortIndirect(
        sortKeys,
        totalActiveSplats,
        roundedNumBits,
        dispatchRange,
        sortSlotBase,
        compaction.sortElementCountBuffer,
        { destructiveKeys: true }
      )
    )
    if (pickMode) this.pickMaterialValue?.setParameter('compactedSplatIds', compactedSplatIds)
    this.setHybridSortedRendering(
      drawRange.base,
      sortedIndices,
      projectionCache,
      compaction.numSplatsBuffer,
      pickMode ? this.pickMeshInstanceValue : this.meshInstance,
      pickMode ? this.pickMaterialValue : this.internalMaterial,
      params
    )
    const pickIds = projector.pickIds
    if (!pickMode) {
      camera.updateMatrixWorld(true)
      this.forwardPreparedView = {
        stateVersion: worldState.version,
        drawSlot: drawRange.base,
        sortedIndices,
        projectionCache,
        numSplatsBuffer: compaction.numSplatsBuffer,
        viewportSize: this.viewportSize.clone(),
        cameraMatrixWorld: camera.matrixWorld.clone(),
        cameraProjectionMatrix: camera.projectionMatrix.clone(),
        alphaClip: params.alphaClip,
        alphaClipForward: params.alphaClipForward,
        cacheStride: this.cacheStride,
        pickIds: this.gpuPickIdMode === 'buffer' ? (pickIds ?? undefined) : undefined,
      }
    }
    return true
  }

  private setHybridSortedRendering(
    drawSlot: number,
    sortedIndices: GSplatBufferLeaf,
    projectionCache: GSplatBufferLeaf,
    numSplatsBuffer: GSplatBufferLeaf,
    drawObject: GSplatDrawObjectLeaf | null = this.meshInstance,
    material: GSplatMaterialLeaf | null = this.internalMaterial,
    params?: GSplatRenderViewParams
  ): void {
    if (!drawObject || !material) throw new Error('GSplatHybridRenderer pick leaves 未准备')
    drawObject.setIndirect(drawSlot)
    material.setParameter('sortedIndices', sortedIndices)
    material.setParameter('projCache', projectionCache)
    material.setParameter('numSplatsStorage', numSplatsBuffer)
    if (material === this.pickMaterialValue) material.setParameter('pickIds', null)
    if (params) {
      material.setParameter('viewport_size', this.viewportSize)
      material.setParameter('alphaClip', params.alphaClip)
      material.setParameter('alphaClipForward', params.alphaClipForward)
    }
    if (material.getDefine('GSPLAT_INDIRECT_DRAW') !== true) {
      material.setDefine('GSPLAT_INDIRECT_DRAW', true)
      material.update()
    }
    drawObject.visible = true
    if (drawObject.instancingCount <= 0) drawObject.instancingCount = 1
  }

  private configureMaterial(): void {
    this.internalMaterial.setDefine('{GSPLAT_INSTANCE_SIZE}', GSPLAT_INSTANCE_SIZE)
    this.internalMaterial.setDefine('{CACHE_STRIDE}', this.cacheStride)
    this.internalMaterial.setDefine('SH_BANDS', '0')
    this.internalMaterial.setDefine('GSPLAT_INDIRECT_DRAW', true)
    this.internalMaterial.setDefine('DITHER_NONE', '')
    this.internalMaterial.setRasterState({
      cullMode: 'none',
      blendMode: 'premultiplied',
      depthTest: true,
      depthWrite: false,
    })
    this.internalMaterial.update()
  }

  private copyMaterialSettings(sourceMaterial: GSplatMaterialLeaf): void {
    const source = sourceMaterial.captureSnapshot(this.adapter.language)
    const current = this.internalMaterial.captureSnapshot(this.adapter.language)
    const sourceDefines = new Map(source.defines)
    for (const [name] of current.defines) {
      if (!this.internalDefines.has(name) && !sourceDefines.has(name)) {
        this.internalMaterial.setDefine(name, undefined)
      }
    }
    for (const [name, value] of source.defines) {
      this.internalMaterial.setDefine(name, value)
    }
    for (const [name, value] of source.parameters) {
      this.internalMaterial.setParameter(name, value)
    }

    const sourceChunksKey = createShaderChunksKey(source)
    if (sourceChunksKey !== this.lastSourceChunksKey) {
      const chunks = this.internalMaterial.getShaderChunks(this.adapter.language)
      chunks.clear()
      for (const [name, value] of source.shaderChunks) chunks.set(name, value)
      this.lastSourceChunksKey = sourceChunksKey
    }
    this.internalMaterial.update()
  }

  /**
   * Pick leaves are lazy and are not part of the forward material owner. Reconcile the current
   * forward effect revision before binding pick-only sorted/cache parameters; otherwise a pick
   * created after an effect/define change would silently rasterize stale shader state.
   */
  private syncPickMaterialSettings(): void {
    const target = this.pickMaterialValue
    if (!target) return
    const source = this.internalMaterial.captureSnapshot(this.adapter.language)
    const current = target.captureSnapshot(this.adapter.language)
    let changed = false
    const sourceDefines = new Map(source.defines)
    for (const [name] of current.defines) {
      if (sourceDefines.has(name) || name.startsWith('PICK_') || name.startsWith('GSPLAT_PICK')) {
        continue
      }
      target.setDefine(name, undefined)
      changed = true
    }
    for (const [name, value] of source.defines) {
      if (
        current.defines.find(
          ([currentName, currentValue]) => currentName === name && currentValue === value
        )
      ) {
        continue
      }
      target.setDefine(name, value)
      changed = true
    }

    const sourceParameters = new Map(source.parameters)
    const pickParameters = new Set([
      'sortedIndices',
      'projCache',
      'numSplatsStorage',
      'compactedSplatIds',
      'pickIds',
    ])
    for (const [name] of current.parameters) {
      if (!sourceParameters.has(name) && !pickParameters.has(name)) {
        target.deleteParameter(name)
        changed = true
      }
    }
    for (const [name, value] of source.parameters) {
      if (
        current.parameters.find(
          ([currentName, currentValue]) => currentName === name && currentValue === value
        )
      ) {
        continue
      }
      target.setParameter(name, value)
      changed = true
    }

    const sourceChunks = new Map(source.shaderChunks)
    const currentChunks = new Map(current.shaderChunks)
    if (
      sourceChunks.size !== currentChunks.size ||
      [...sourceChunks].some(([name, value]) => currentChunks.get(name) !== value)
    ) {
      const chunks = target.getShaderChunks(this.adapter.language)
      chunks.clear()
      for (const [name, value] of source.shaderChunks) chunks.set(name, value)
      changed = true
    }
    if (changed) target.update()
  }

  private computeDistanceRange(
    worldState: GSplatWorldState,
    camera: THREE.Camera,
    radialSort: boolean
  ): { readonly minDist: number; readonly maxDist: number } {
    camera.updateMatrixWorld(true)
    camera.getWorldPosition(this.cameraPosition)
    this.cameraDirection.setFromMatrixColumn(camera.matrixWorld, 2).normalize()
    let minDist = radialSort ? 0 : Number.POSITIVE_INFINITY
    let maxDist = radialSort ? 0 : Number.NEGATIVE_INFINITY
    for (const splat of worldState.splats) {
      splat.node.updateWorldMatrix(true, false)
      for (let corner = 0; corner < 8; corner++) {
        this.aabbCorner.set(
          corner & 1 ? splat.aabb.max.x : splat.aabb.min.x,
          corner & 2 ? splat.aabb.max.y : splat.aabb.min.y,
          corner & 4 ? splat.aabb.max.z : splat.aabb.min.z
        )
        this.aabbCorner.applyMatrix4(splat.node.matrixWorld)
        const distance = radialSort
          ? this.aabbCorner.distanceTo(this.cameraPosition)
          : this.aabbCorner.sub(this.cameraPosition).dot(this.cameraDirection)
        minDist = Math.min(minDist, distance)
        maxDist = Math.max(maxDist, distance)
      }
    }
    if (!Number.isFinite(minDist) || !Number.isFinite(maxDist) || maxDist <= minDist) {
      return { minDist: 0, maxDist: 1 }
    }
    return { minDist, maxDist }
  }

  private requireGpuSorter(): Required<
    Pick<GSplatHybridGpuSorter, 'resolvedKind' | 'radixBits' | 'prepareIndirect' | 'sortIndirect'>
  > {
    const sorter = this.gpuSorter
    if (!sorter || !sorter.radixBits || !sorter.sortIndirect) {
      throw new Error('GSplatHybridRenderer sorter 尚未实现 GPU indirect sort')
    }
    return sorter as Required<
      Pick<GSplatHybridGpuSorter, 'resolvedKind' | 'radixBits' | 'prepareIndirect' | 'sortIndirect'>
    >
  }

  private requireProjector(): GSplatHybridProjectorLeaf &
    Required<Pick<GSplatHybridProjectorLeaf, 'dispatch' | 'writeIndirectArgs'>> {
    const projector = this.projector
    if (!projector?.dispatch || !projector.writeIndirectArgs) {
      throw new Error('GSplatHybridRenderer projector pipeline 不完整')
    }
    return projector as GSplatHybridProjectorLeaf &
      Required<Pick<GSplatHybridProjectorLeaf, 'dispatch' | 'writeIndirectArgs'>>
  }

  private requireIntervalCompaction(): Required<
    Pick<
      GSplatHybridIntervalCompactionLeaf,
      | 'compactedSplatIds'
      | 'numSplatsBuffer'
      | 'sortElementCountBuffer'
      | 'uploadIntervals'
      | 'dispatchCompact'
      | 'writeIndirectArgs'
    >
  > {
    const compaction = this.intervalCompaction
    if (
      !compaction?.uploadIntervals ||
      !compaction.dispatchCompact ||
      !compaction.numSplatsBuffer ||
      !compaction.sortElementCountBuffer
    ) {
      throw new Error('GSplatHybridRenderer interval pipeline 不完整')
    }
    return compaction as Required<
      Pick<
        GSplatHybridIntervalCompactionLeaf,
        | 'compactedSplatIds'
        | 'numSplatsBuffer'
        | 'sortElementCountBuffer'
        | 'uploadIntervals'
        | 'dispatchCompact'
        | 'writeIndirectArgs'
      >
    >
  }
}

function createShaderChunksKey(snapshot: GSplatMaterialSnapshot): string {
  return snapshot.shaderChunks
    .map(([name, source]) => `${name.length}:${name}:${source.length}:${source}`)
    .join('|')
}
